/**
 * Picking Workflow Simulator
 * Simulates the complete picking workflow using real API calls
 */

import { getSharedRateLimiter } from '../services/RateLimiter';
import { getSharedLockManager } from '../services/LockManager';
import { VirtualOperator } from '../services/VirtualOperator';
import { AuthService } from '../../../../../services/authService';
import { SimulatorConfig } from '../types';

// API base URL - use environment variable for production deployments
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Authenticated fetch helper using AuthService
const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  return AuthService.authenticatedFetch(url, options);
};

export interface PickingWorkflowResult {
  success: boolean;
  pListId?: number;
  linesProcessed: number;
  error?: string;
  duration: number;
}

export interface PListLineForSimulation {
  pListLineNo: number;
  itemNo: string;
  qty: number;
  qtyPicked: number;
  binCode: string;
}

export class PickingWorkflow {
  private config: SimulatorConfig;
  private operator: VirtualOperator;
  private rateLimiter = getSharedRateLimiter();
  private lockManager: ReturnType<typeof getSharedLockManager>;

  constructor(config: SimulatorConfig, operator: VirtualOperator) {
    this.config = config;
    this.operator = operator;
    this.lockManager = getSharedLockManager(config.companyNo);
  }

  /**
   * Execute one complete picking workflow cycle
   */
  async execute(): Promise<PickingWorkflowResult> {
    const startTime = Date.now();
    let pListId: number | undefined;
    let linesProcessed = 0;

    try {
      // Step 1: Get available picking lists (status 10 - Approved)
      this.operator.startTask('picking', 'Henter tilgjengelige plukklister');
      await this.rateLimiter.throttle();

      let availableLists = await this.getAvailablePickingLists();

      // If no picking lists and auto-generation is enabled, generate orders
      if (availableLists.length === 0 && this.config.workflows.picking.ordersToGenerate > 0) {
        this.operator.startTask('picking', 'Genererer salgsordrer...');
        await this.rateLimiter.throttle();

        const generated = await this.generateOrdersAndPickLists();
        if (generated) {
          // Wait for orders to be created, then fetch lists again
          await this.delay(1000);
          availableLists = await this.getAvailablePickingLists();
        }
      }

      if (availableLists.length === 0) {
        // No picking lists available - this is expected sometimes
        return {
          success: true,
          linesProcessed: 0,
          duration: Date.now() - startTime,
          error: 'Ingen tilgjengelige plukklister'
        };
      }

      // Step 2: Select a random list and lock it
      const selectedList = availableLists[Math.floor(Math.random() * availableLists.length)];
      pListId = selectedList.pListID ?? selectedList.pListId ?? selectedList.PListId;

      if (pListId === undefined) {
        return {
          success: false,
          linesProcessed: 0,
          duration: Date.now() - startTime,
          error: 'Kunne ikke finne pListId i valgt liste'
        };
      }

      this.operator.startTask('picking', `Låser plukkliste ${pListId}`);
      await this.rateLimiter.throttle();

      const locked = await this.lockManager.acquireLock(pListId, this.operator.getId());
      if (!locked) {
        return {
          success: false,
          pListId,
          linesProcessed: 0,
          duration: Date.now() - startTime,
          error: 'Kunne ikke låse plukkliste'
        };
      }

      try {
        // Step 3: Start picking
        this.operator.startTask('picking', `Starter plukking av ${pListId}`);
        await this.rateLimiter.throttle();
        await this.startPicking(pListId);

        // Step 4: Get picking list details
        this.operator.startTask('picking', `Henter detaljer for ${pListId}`);
        await this.rateLimiter.throttle();
        const details = await this.getPickingDetails(pListId);

        // Step 5: Process each line
        const lines = details.lines || [];
        for (const line of lines) {
          // Skip already fully picked lines
          if (line.qtyPicked >= line.qty) {
            continue;
          }

          this.operator.startTask('picking', `Plukker linje ${line.pListLineNo}`);

          // Simulate walking to location
          await this.delay(this.config.timing.walkTimeMs);

          // Simulate picking
          await this.delay(this.config.timing.pickTimeMs);

          // Update picked quantity
          await this.rateLimiter.throttle();
          await this.updatePickedQuantity(pListId, line.pListLineNo, line.qty);

          linesProcessed++;
        }

        // Step 6: Complete picking
        this.operator.startTask('picking', `Fullfører plukking av ${pListId}`);
        await this.rateLimiter.throttle();
        await this.completePicking(pListId);

        // Step 7: Optionally send to packing
        if (this.config.workflows.packing.enabled && Math.random() > 0.5) {
          this.operator.startTask('picking', `Sender ${pListId} til pakking`);
          await this.rateLimiter.throttle();
          await this.sendToPacking(pListId);
        }

        return {
          success: true,
          pListId,
          linesProcessed,
          duration: Date.now() - startTime
        };

      } finally {
        // Always release the lock
        await this.lockManager.releaseLock(pListId, this.operator.getId());
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ukjent feil';
      console.error(`[PickingWorkflow] Error:`, errorMessage);

      return {
        success: false,
        pListId,
        linesProcessed,
        duration: Date.now() - startTime,
        error: errorMessage
      };
    }
  }

  /**
   * Get available picking lists (status 10 - Approved)
   */
  private async getAvailablePickingLists(): Promise<any[]> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/picking/lists/by-status?status=10`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch available picking lists: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get picking list details
   */
  private async getPickingDetails(pListId: number): Promise<any> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/picking/lists/${pListId}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch picking details: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Start picking
   */
  private async startPicking(pListId: number): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/picking/lists/${pListId}/start`,
      {
        method: 'POST',
        body: JSON.stringify({ operatorId: this.operator.getId() })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to start picking: ${response.statusText}`);
    }
  }

  /**
   * Update picked quantity for a line
   */
  private async updatePickedQuantity(
    pListId: number,
    lineNo: number,
    qtyPicked: number
  ): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/picking/lists/${pListId}/lines`,
      {
        method: 'PUT',
        body: JSON.stringify({
          lineNo,
          qtyPicked,
          operatorId: this.operator.getId(),
          startDateTime: new Date().toISOString()
        })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to update picked quantity: ${response.statusText}`);
    }
  }

  /**
   * Complete picking
   */
  private async completePicking(pListId: number): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/picking/lists/${pListId}/complete`,
      {
        method: 'POST',
        body: JSON.stringify({ operatorId: this.operator.getId() })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to complete picking: ${response.statusText}`);
    }
  }

  /**
   * Send to packing
   */
  private async sendToPacking(pListId: number): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/picking/lists/${pListId}/to-packing`,
      {
        method: 'POST',
        body: JSON.stringify({ operatorId: this.operator.getId() })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to send to packing: ${response.statusText}`);
    }
  }

  /**
   * Generate orders and create picking lists using RuleGroupEngine
   */
  private async generateOrdersAndPickLists(): Promise<boolean> {
    try {
      const ruleGroupId = this.config.workflows.picking.ruleGroupId;

      // Check if rule group is configured
      if (!ruleGroupId || ruleGroupId === 0) {
        console.warn('[PickingWorkflow] No rule group configured for picking list creation');
        return false;
      }

      const ordersToGenerate = this.config.workflows.picking.ordersToGenerate;
      console.log(`[PickingWorkflow] Generating ${ordersToGenerate} orders...`);

      // Step 1: Generate random orders (API generates one order per call)
      const generatedOrderNos: string[] = [];

      for (let i = 0; i < ordersToGenerate; i++) {
        await this.rateLimiter.throttle();

        const orderResponse = await authenticatedFetch(
          `${API_BASE_URL}/orders/generate-random`,
          {
            method: 'POST',
            body: JSON.stringify({
              NumberOfLines: Math.floor(Math.random() * 3) + 2, // 2-4 lines per order
              AllowItemsWithoutStock: false,
              Warehouse: this.config.warehouseId,
              MaxQuantityPerLine: 10
            })
          }
        );

        if (orderResponse.ok) {
          const orderResult = await orderResponse.json();
          // API returns { order: { orderNo: "...", ... }, lines: [...], message: "..." }
          const orderNo = orderResult.order?.orderNo || orderResult.Order?.OrderNo;
          if (orderNo) {
            generatedOrderNos.push(orderNo);
          }
        } else {
          console.warn(`[PickingWorkflow] Failed to generate order ${i + 1}:`, orderResponse.statusText);
        }
      }

      if (generatedOrderNos.length === 0) {
        console.warn('[PickingWorkflow] No orders were generated');
        return false;
      }

      console.log(`[PickingWorkflow] Generated ${generatedOrderNos.length} orders: ${generatedOrderNos.join(', ')}. Executing rule group to create picking lists...`);

      // Step 2: Execute RuleGroupEngine to create picking lists from new orders
      await this.rateLimiter.throttle();

      // Generate a unique session ID for this simulation run
      const sessionId = `SIM-${this.operator.getId()}-${Date.now()}`;

      const ruleEngineResponse = await authenticatedFetch(
        `${API_BASE_URL}/${this.config.companyNo}/rulegroupengine/execute`,
        {
          method: 'POST',
          body: JSON.stringify({
            RuleGroupID: ruleGroupId,
            CompanyNo: this.config.companyNo,
            WarehouseID: this.config.warehouseId,
            SessionID: sessionId
          })
        }
      );

      if (!ruleEngineResponse.ok) {
        const errorText = await ruleEngineResponse.text().catch(() => ruleEngineResponse.statusText);
        console.warn('[PickingWorkflow] Failed to execute rule group:', errorText);
        return false;
      }

      const ruleResult = await ruleEngineResponse.json().catch(() => ({}));
      console.log('[PickingWorkflow] RuleGroupEngine executed successfully:', ruleResult);
      return true;

    } catch (error) {
      console.error('[PickingWorkflow] Error generating orders:', error);
      return false;
    }
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default PickingWorkflow;
